import { useState, useEffect, useRef, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { 
  Lock, 
  Send, 
  Shield, 
  Database, 
  Terminal,
  ChevronRight,
  CheckCircle,
  XCircle,
  Music,
  Users,
  Disc3,
  Archive,
  Mail,
  Fingerprint,
  Rocket,
  Coins,
  ChevronDown,
  Globe,
  Link2,
  ShoppingBag
} from 'lucide-react';
import { useTenant, getAdminAsSlug, setAdminAsSlug, clearAdminAsSlug } from './hooks/useTenant';
import ArtistPage from './pages/ArtistPage';
import SignUpPage from './pages/SignUpPage';
import RequestAccessPage from './pages/RequestAccessPage';
import ArtistEnrolmentPage from './pages/ArtistEnrolmentPage';
import PlayerPage from './pages/PlayerPage';
import TrackEditorPage from './pages/TrackEditorPage';
import ManifestoPage from './pages/ManifestoPage';
import TenantLoginPage from './pages/TenantLoginPage';
import GrowthPage from './pages/GrowthPage';
import CapturePage from './pages/CapturePage';
import CatalogPage from './pages/CatalogPage';
import FanZonePage from './pages/FanZonePage';
import FansPage from './pages/FansPage';
import TransmissionsPage from './pages/TransmissionsPage';
import TransmissionEditorPage from './pages/TransmissionEditorPage';
import PressingsPage from './pages/PressingsPage';
import VaultEditorPage from './pages/VaultEditorPage';
import ViewVaultPage from './pages/ViewVaultPage';
import ArtistProfilePage from './pages/ArtistProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTemplatesPage from './pages/admin/AdminTemplatesPage';
import AdminMessagesPage from './pages/admin/AdminMessagesPage';
import AdminMintQueuePage from './pages/admin/AdminMintQueuePage';
import AdminCompliancePage from './pages/admin/AdminCompliancePage';
import AdminComplianceAuditPage from './pages/admin/AdminComplianceAuditPage';
import FanLoginPage from './pages/FanLoginPage';
import { TenantProvider } from './context/TenantContext';
import { useAuthStore } from './store/authStore';
import { getMe, checkAccess, notifyFanRegistrationInterest } from './services/api';
import OnboardingGuard from './components/dashboard/OnboardingGuard';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

// --- COMPONENTS ---

function Navbar({
  onLaunch,
  ctaLabel = 'Artist Login',
  ctaHref = '/login',
  showCta = true,
  activeNav,
}: {
  onLaunch?: () => void;
  ctaLabel?: string;
  ctaHref?: string;
  showCta?: boolean;
  activeNav?: 'for-fans' | 'for-artists' | 'manifesto';
}) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
          <span className="font-mono font-bold text-lg tracking-tight">fans<span className="text-electric-cyan">.audio</span></span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          <a href="/for-fans" className={`text-sm transition-colors ${activeNav === 'for-fans' ? 'text-electric-cyan' : 'text-text-secondary hover:text-electric-cyan'}`}>For Fans</a>
          <a href="/for-artists" className={`text-sm transition-colors ${activeNav === 'for-artists' ? 'text-electric-cyan' : 'text-text-secondary hover:text-electric-cyan'}`}>For Artists</a>
          <a href="/manifesto" className={`text-sm transition-colors ${activeNav === 'manifesto' ? 'text-electric-cyan' : 'text-text-secondary hover:text-electric-cyan'}`}>Manifesto</a>
        </div>
        {showCta && (onLaunch ? (
          <button onClick={onLaunch} className="btn-primary text-sm !px-5 !py-2.5">
            {ctaLabel}
          </button>
        ) : (
          <a href={ctaHref} className="btn-primary text-sm !px-5 !py-2.5 inline-flex items-center justify-center">
            {ctaLabel}
          </a>
        ))}
      </div>
    </nav>
  );
}

function AnimatedVUMeter() {
  return (
    <div className="mt-16 max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <span className="terminal-label">output_level</span>
        <span className="w-2 h-2 rounded-full bg-electric-cyan animate-pulse-cyan" />
      </div>
      <div className="flex items-end gap-[2px] h-8">
        {Array.from({ length: 24 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 rounded-sm animate-vu-bar"
            style={{
              animationDelay: `${i * 0.07}s`,
              animationDuration: `${0.8 + Math.random() * 0.6}s`,
              background: i < 16 ? '#00F5FF' : i < 20 ? '#FFD700' : '#FF4444',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function HomeHero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden waveform-bg">
      <div className="absolute inset-0 bg-gradient-to-b from-deep-charcoal via-transparent to-deep-charcoal" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[520px] h-[520px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-24">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="mb-5 sm:mb-6 flex justify-center">
            <img
              src="/fans-audio-logo.png"
              alt="fans.audio logo"
              className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain drop-shadow-[0_0_18px_rgba(0,245,255,0.25)]"
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="mb-6">
            <span className="terminal-label inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-electric-cyan/20 bg-electric-cyan/5">
              FANS.AUDIO
            </span>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
            Music belongs between artists and fans.
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-8 leading-relaxed">
            fans.audio is a place for independent artists to build a direct relationship with the people who care about their music.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-base sm:text-lg text-text-secondary/90 max-w-3xl mx-auto mb-10 leading-relaxed">
            No algorithm deciding who gets heard.<br className="hidden sm:block" />
            No rented audience.<br className="hidden sm:block" />
            No endless collection of services just to release a record.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/for-fans" className="btn-secondary text-base inline-flex items-center">
              For Fans
            </a>
            <a href="/for-artists" className="btn-primary text-base inline-flex items-center">
              For Artists
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function HomeConceptSection() {
  const cards = [
    {
      label: 'music',
      heading: 'Discover music from independent artists.',
      copy: 'Listen. Follow. Collect releases you love. Not because an algorithm put them in front of you, but because you chose to be there.',
    },
    {
      label: 'the_relationship',
      heading: 'Follow the artist, not the platform.',
      copy: 'When an artist you follow releases something, writes something or wants to share something, they can reach you directly. That relationship belongs to the artist and the fan.',
    },
    {
      label: 'the_collection',
      heading: 'Music can be more than a stream.',
      copy: 'Artists can release permanent digital editions - Pressings - that fans can add to their collection. The music remains part of the artist\'s catalogue. Your collection remains yours.',
    },
    {
      label: 'the_community',
      heading: 'Discovery should be something we do together.',
      copy: 'Artists introduce fans to other artists. Fans discover music through people they trust. Audiences overlap. Scenes develop. Music travels.',
    },
  ];

  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-40" />
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-xl sm:text-2xl font-semibold text-white leading-relaxed">
              The artist owns the relationship.<br />
              The fan chooses the relationship.
            </p>
            <p className="text-text-secondary mt-4 text-lg">fans.audio is the infrastructure between them.</p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cards.map((item) => (
              <motion.article key={item.label} variants={fadeInUp} className="glass-card-hover p-7">
                <p className="terminal-label mb-3">{item.label}</p>
                <h3 className="text-2xl font-bold mb-3 leading-snug">{item.heading}</h3>
                <p className="text-text-secondary leading-relaxed">{item.copy}</p>
              </motion.article>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function HomeSplitCta() {
  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 waveform-bg opacity-40" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="terminal-label">path::selection</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 mb-4">Two sides of the same idea.</h2>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.article variants={fadeInUp} className="glass-card p-8">
              <h3 className="text-2xl font-bold mb-3">I'm a fan</h3>
              <p className="text-text-secondary mb-6">Discover artists, follow their work and build a collection.</p>
              <a href="/for-fans" className="btn-secondary inline-flex items-center">For Fans</a>
            </motion.article>

            <motion.article variants={fadeInUp} className="glass-card p-8 border-electric-cyan/20">
              <h3 className="text-2xl font-bold mb-3">I'm an artist</h3>
              <p className="text-text-secondary mb-6">Own your catalogue, your audience and the infrastructure connecting them.</p>
              <a href="/for-artists" className="btn-primary inline-flex items-center">For Artists</a>
            </motion.article>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-deep-charcoal">
      <Navbar ctaLabel="Artist Login" ctaHref="/login" showCta={false} />
      <HomeHero />
      <HomeConceptSection />
      <HomeSplitCta />
      <Footer />
    </div>
  );
}

function ForFansHero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden waveform-bg">
      <div className="absolute inset-0 bg-gradient-to-b from-deep-charcoal via-transparent to-deep-charcoal" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-electric-cyan/5 rounded-full blur-[120px]" />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center pt-24">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="mb-5 sm:mb-6 flex justify-center">
            <img
              src="/fans-audio-logo.png"
              alt="fans.audio logo"
              className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[72px] lg:h-[72px] object-contain drop-shadow-[0_0_16px_rgba(0,245,255,0.22)]"
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="mb-6">
            <span className="terminal-label inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-electric-cyan/20 bg-electric-cyan/5">
              FOR FANS
            </span>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.1] tracking-tight mb-6">
            Music without the feed.
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-10 leading-relaxed">
            Follow artists because you want to hear from them.
            <br className="hidden sm:block" />
            When they release music, you'll know.
            <br className="hidden sm:block" />
            When they have something to say, you'll hear it from them.
            <br className="hidden sm:block" />
            No algorithmic timeline. No engagement games. No one deciding what should come next.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              disabled
              className="btn-secondary text-base opacity-50 cursor-not-allowed"
              title="Fan sign-up coming soon"
            >
              Fan Sign-up Coming Soon
            </button>
            <a href="/fan-login" className="btn-primary text-base inline-flex items-center">Fan Login</a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ForFansConcepts() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmitNotify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setSubmitState('error');
      setSubmitMessage('Enter your email address to join the notify list.');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitState('idle');
      setSubmitMessage('');

      await notifyFanRegistrationInterest(trimmedEmail);
      setSubmitState('success');
      setSubmitMessage("You're on the list. We'll let you know when fan registration opens.");
      setEmail('');
    } catch {
      setSubmitState('error');
      setSubmitMessage('We could not register your interest right now. Please try again shortly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const items = [
    {
      label: 'follow',
      heading: 'A follow means something.',
      copy: 'You\'re giving an artist permission to keep you informed. Not hoping an algorithm happens to show you their next release.',
      icon: Users,
    },
    {
      label: 'listen',
      heading: 'Hear the music.',
      copy: 'This is still music. We\'re not trying to turn every interaction into a transaction. Listen to artists. Explore their catalogue. Discover somebody new.',
      icon: Music,
    },
    {
      label: 'collect',
      heading: 'Keep the music that matters.',
      copy: 'When an artist releases a Pressing, you can add that edition to your personal collection. It isn\'t another monthly subscription. It\'s yours.',
      icon: Archive,
    },
  ];

  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-40" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {items.map((item) => (
              <motion.article key={item.label} variants={fadeInUp} className="glass-card-hover p-6">
                <item.icon className="w-7 h-7 text-electric-cyan mb-4" />
                <p className="terminal-label mb-2">{item.label}</p>
                <h3 className="text-xl font-bold mb-3 leading-snug">{item.heading}</h3>
                <p className="text-text-secondary leading-relaxed text-sm">{item.copy}</p>
              </motion.article>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="text-center mt-14 mb-14">
            <p className="text-2xl sm:text-3xl font-bold leading-snug">
              Streaming made music infinite.
              <br />
              Collections make it personal again.
            </p>
          </motion.div>

          <motion.article variants={fadeInUp} className="glass-card p-8 text-center max-w-3xl mx-auto">
            <p className="terminal-label mb-3">fan::registration</p>
            <h3 className="text-3xl font-bold mb-4">Fan registration is opening soon.</h3>
            <p className="text-text-secondary leading-relaxed mb-6">
              Join the notify list and we'll email you as soon as registration is available.
            </p>

            <form onSubmit={handleSubmitNotify} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                className="flex-1 rounded-lg bg-black/30 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-electric-cyan"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary !px-6 !py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Joining...' : 'Notify Me'}
              </button>
            </form>

            {submitState !== 'idle' && (
              <p className={`mt-4 text-sm ${submitState === 'success' ? 'text-electric-cyan' : 'text-red-400'}`}>
                {submitMessage}
              </p>
            )}
          </motion.article>
        </motion.div>
      </div>
    </section>
  );
}

function ForFansPage() {
  return (
    <div className="min-h-screen bg-deep-charcoal">
      <Navbar ctaLabel="Fan Login" ctaHref="/fan-login" activeNav="for-fans" />
      <ForFansHero />
      <ForFansConcepts />
      <Footer />
    </div>
  );
}

function HeroSection({ onLaunch }: { onLaunch?: () => void }) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden waveform-bg">
      <div className="absolute inset-0 bg-gradient-to-b from-deep-charcoal via-transparent to-deep-charcoal" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-24">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="mb-5 sm:mb-6 flex justify-center">
            <img
              src="/fans-audio-logo.png"
              alt="fans.audio logo"
              className="w-14 h-14 sm:w-16 sm:h-16 lg:w-[72px] lg:h-[72px] object-contain drop-shadow-[0_0_16px_rgba(0,245,255,0.22)]"
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="mb-6">
            <span className="terminal-label inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-electric-cyan/20 bg-electric-cyan/5">
              FOR INDEPENDENT ARTISTS
            </span>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
            Give your music a home.
            <br />
            Bring your fans with you.
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-6 leading-relaxed">
            Create a home for your music, share your releases and build a direct relationship with the people who care about what you create.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-base sm:text-lg text-text-secondary/90 max-w-3xl mx-auto mb-10 leading-relaxed">
            Keep using the platforms that help people discover your music. Give those listeners somewhere worth coming back to.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={onLaunch} className="btn-primary text-base inline-flex items-center">
              <Terminal className="w-5 h-5 mr-2" />
              Apply to Join
            </button>
            <a href="#how-it-works" className="btn-secondary text-base inline-flex items-center">
              How It Works
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <div className="mt-7 sm:mt-8 mb-3 flex justify-center">
              <span className="terminal-label inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-electric-cyan/20 bg-electric-cyan/5">
                <span className="w-2 h-2 rounded-full bg-electric-cyan animate-pulse-cyan" />
                artist::home
              </span>
            </div>
            <AnimatedVUMeter />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ArtistBridgeSection() {
  return (
    <section id="how-it-works" className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-40" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Everything starts with your music.</h2>
            <p className="text-text-secondary text-lg leading-relaxed">
              Your music deserves more than a passing moment in someone's feed. Give it a place where listeners can explore your work, discover your releases and get to know the artist behind them.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                label: 'YOUR PROFILE',
                heading: 'A place to call your own.',
                copy: 'Bring your music, identity and story together in one artist profile. A destination you can share wherever people discover you.',
              },
              {
                label: 'YOUR CATALOGUE',
                heading: 'Your music, organised.',
                copy: "Build a catalogue of your tracks and releases. Give listeners somewhere to explore what you've created, from your latest work to earlier recordings.",
              },
              {
                label: 'YOUR PRESSINGS',
                heading: 'Releases worth keeping.',
                copy: "Create digital editions of your music that fans can add to their personal collections. Give each release an identity and a lasting place in your fans' musical lives.",
              },
            ].map((item) => (
              <motion.article key={item.label} variants={fadeInUp} className="glass-card-hover p-6">
                <p className="terminal-label mb-2">{item.label}</p>
                <h3 className="text-xl font-bold mb-3 leading-snug">{item.heading}</h3>
                <p className="text-text-secondary leading-relaxed text-sm">{item.copy}</p>
              </motion.article>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function BuildSomethingYouOwnSection() {
  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-30" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Turn listeners into fans.</h2>
            <p className="text-text-secondary text-lg leading-relaxed">
              Every new listener is an opportunity to build a relationship. fans.audio helps you bring people from the places they discover your music into a space where they can explore, follow and collect your work.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '1. SHARE',
                heading: 'Take your music wherever your audience is.',
                copy: 'Use Captures to share moments of your music through your existing promotional channels, giving listeners a reason to discover more.',
                icon: Send,
              },
              {
                step: '2. DISCOVER',
                heading: 'Give listeners somewhere to go.',
                copy: 'Bring people back to your artist profile and releases, where they can listen, explore your catalogue and discover more of your work.',
                icon: Music,
              },
              {
                step: '3. CONNECT',
                heading: 'Turn interest into a relationship.',
                copy: 'Fans can follow your work and add Pressings to their collections, creating a connection that lasts beyond the original share.',
                icon: Users,
              },
            ].map((item) => (
              <motion.article key={item.step} variants={fadeInUp} className="glass-card-hover p-6">
                <item.icon className="w-7 h-7 text-electric-cyan mb-4" />
                <p className="terminal-label mb-2">{item.step}</p>
                <h3 className="text-xl font-bold mb-3 leading-snug">{item.heading}</h3>
                <p className="text-text-secondary leading-relaxed text-sm">{item.copy}</p>
              </motion.article>
            ))}
          </motion.div>

          <motion.p variants={fadeInUp} className="text-center text-white font-semibold mt-10 text-lg">
            Share - Discover - Connect
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function AudienceRelationshipSection() {
  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-25" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Build your audience. Keep your audience.</h2>
            <p className="text-text-secondary text-lg leading-relaxed mb-4">
              Followers on social platforms are useful, but they are not the whole relationship. On fans.audio, people choose to connect with you because they care about your music.
            </p>
            <p className="text-text-secondary text-lg leading-relaxed">
              Know who follows your work, see how listeners engage with your releases and communicate directly with fans who have chosen to hear from you.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'REAL FANS',
                copy: "Build an audience around people who have actively chosen to follow or collect your music.",
                icon: Users,
              },
              {
                title: 'MEANINGFUL INSIGHT',
                copy: 'Understand how people discover, listen to and engage with your work.',
                icon: Disc3,
              },
              {
                title: 'DIRECT COMMUNICATION',
                copy: 'Share releases, updates and important moments with the fans who want to hear from you.',
                icon: Mail,
              },
            ].map((item) => (
              <motion.article key={item.title} variants={fadeInUp} className="glass-card-hover p-6">
                <item.icon className="w-7 h-7 text-electric-cyan mb-4" />
                <p className="terminal-label mb-2">{item.title}</p>
                <p className="text-text-secondary leading-relaxed text-sm">{item.copy}</p>
              </motion.article>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function SovereignFeatureGrid() {
  const features = [
    {
      num: '01',
      title: 'The Catalog',
      pitch: 'Your entire discography, unified.',
      utility: 'A high-performance dashboard to manage every master, stem, and metadata point. No more scattered folders or lost WAVs. This is your central source of truth.',
      icon: Disc3,
      comingSoon: false,
    },
    {
      num: '02',
      title: 'The Vaults',
      pitch: 'Immutable Digital Pressings.',
      utility: 'Create secure, high-fidelity containers for 24-bit masters, stems, and exclusive archives. Once locked, they become permanent assets for your Fan Circle to unlock and collect.',
      icon: Archive,
      comingSoon: false,
    },
    {
      num: '03',
      title: 'Transmissions',
      pitch: 'Unmediated Communication.',
      utility: 'A professional-grade editor to send high-fidelity updates directly to your fans\' inboxes. No algorithms, no \"shadow-banning,\" just a direct line from your desk to their device.',
      icon: Mail,
      comingSoon: false,
    },
    {
      num: '04',
      title: 'Forensics',
      pitch: 'The Authentic Signal.',
      utility: 'Active intelligent processing that protects your work. We verify human creation and provide deep analytics into how your audience is actually engaging with the audio, not just \"vanity skips.\"',
      icon: Fingerprint,
      comingSoon: false,
    },
    {
      num: '05',
      title: 'Growth Engine',
      pitch: 'Reclaim the Funnel.',
      utility: 'Built-in tools to build your audience without the \"Link Tax.\" Generate custom lead-capture pages and reciprocal growth links that feed directly into your Sovereign database.',
      icon: Rocket,
      comingSoon: false,
    },
    {
      num: '06',
      title: 'Monetization',
      pitch: 'Artists need to earn.',
      utility: 'We\'re artists too. We\'re building ways for fans to support the artists they value, directly through the platform.',
      icon: Coins,
      comingSoon: true,
    },
  ];

  return (
    <section className="relative py-24 sm:py-32" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(#00FFFF 1px, transparent 1px), linear-gradient(90deg, #00FFFF 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-4">
            <span className="terminal-label">platform::integrated</span>
          </motion.div>

          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            One Place to Build From.
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-text-secondary text-lg max-w-3xl mb-8 leading-relaxed">
            Your website. Your catalogue. Your fans. Your communications. Your releases. Your sales.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-text-secondary text-lg max-w-3xl mb-14 leading-relaxed">
            Not six disconnected services holding different pieces of your career.
            <br className="hidden sm:block" />
            One Sovereign Artist OS you build from.
          </motion.p>

          <motion.div
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.num}
                variants={fadeInUp}
                className={`relative group rounded-lg border p-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,255,255,0.08)] ${
                  feature.comingSoon
                    ? 'border-[#00FFFF]/10 bg-[#0A0A0A]'
                    : 'border-[#00FFFF]/20 bg-[#0D0D0D]'
                }`}
                style={{ backdropFilter: 'blur(4px)' }}
              >
                {/* Coming Soon Badge */}
                {feature.comingSoon && (
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#00FFFF]/20 bg-[#00FFFF]/5">
                    <Lock className="w-3 h-3 text-[#00FFFF]/70" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#00FFFF]/70">Status: Integrating</span>
                  </div>
                )}

                {/* Number + Icon */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-xs text-[#00FFFF]/50 font-bold">{feature.num}</span>
                  <feature.icon className={`w-6 h-6 transition-colors duration-300 ${
                    feature.comingSoon ? 'text-[#00FFFF]/40' : 'text-[#00FFFF] group-hover:text-[#00FFFF]'
                  }`} />
                </div>

                {/* Title */}
                <h3 className={`text-lg font-semibold mb-1 ${
                  feature.comingSoon ? 'text-white/60' : 'text-white'
                }`}>
                  {feature.title}
                </h3>

                {/* Pitch */}
                <p className="font-mono text-xs text-[#00FFFF]/80 uppercase tracking-wide mb-3">
                  {feature.pitch}
                </p>

                {/* Utility */}
                <p className={`text-sm leading-relaxed ${
                  feature.comingSoon ? 'text-text-secondary/60' : 'text-text-secondary'
                }`}>
                  {feature.utility}
                </p>

                {/* Hover glow line at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00FFFF]/0 to-transparent group-hover:via-[#00FFFF]/40 transition-all duration-500" />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function CoreLogicSection() {
  return (
    <section id="pillars" className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient" />
      <div className="relative z-10 max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-4">
            <span className="terminal-label">sovereign::pillars</span>
          </motion.div>

          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            The Working Model
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-text-secondary text-lg max-w-2xl mb-12">
            These are not isolated products. They are coordinated parts of one system that helps you build an asset over time.
          </motion.p>

          <motion.div 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              {
                icon: Music,
                title: 'Create',
                desc: 'Make the music. Build the catalogue. Create the Pressing.',
              },
              {
                icon: Send,
                title: 'Transmit',
                desc: 'Put it into the world. Send it to your audience and the places people already are.',
              },
              {
                icon: Users,
                title: 'Capture',
                desc: 'Turn passing attention into a direct relationship by offering the fan a Pressing to collect.',
              },
              {
                icon: Archive,
                title: 'Collect',
                desc: 'The fan gets something worth keeping: their uniquely minted copy, permanently part of their collection.',
              },
              {
                icon: Link2,
                title: 'Connect',
                desc: 'Continue the relationship directly through future releases and Transmissions.',
              },
            ].map((pillar) => (
              <motion.div key={pillar.title} variants={fadeInUp} className="glass-card-hover p-6">
                <pillar.icon className="w-8 h-8 text-electric-cyan mb-4" />
                <h3 className="text-lg font-semibold mb-2">{pillar.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}


function CostComparisonSection() {
  return (
    <section id="pricing" className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-50" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="terminal-label mb-4 block">cost::analysis</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Stop Renting Your Career.
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto leading-relaxed">
              The problem is not that artists use tools.
              <br className="hidden sm:block" />
              It's that the things they build with those tools often remain dependent on the companies providing them.
            </p>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto mt-4 leading-relaxed">
              A website subscription ends, the website disappears.
              <br className="hidden sm:block" />
              A mailing platform closes the account, the audience becomes inaccessible.
              <br className="hidden sm:block" />
              A social platform changes its algorithm, reach disappears overnight.
            </p>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto mt-4 leading-relaxed">
              fans.audio gives you infrastructure designed to become more yours as you build on it, not less.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* The Old Way */}
            <div className="glass-card p-6 sm:p-8 border-red-500/20">
              <div className="flex items-center gap-2 mb-6">
                <XCircle className="w-5 h-5 text-red-400" />
                <h3 className="font-mono text-sm text-red-400 uppercase tracking-wider">The Conventional Stack</h3>
              </div>
              <div className="space-y-4">
                {[
                  { name: "Email Marketing", cost: "$40/mo" },
                  { name: "Data Analytics", cost: "$50/mo" },
                  { name: "Link Generators", cost: "$20/mo" },
                  { name: "File Hosting", cost: "$15/mo" },
                ].map((item) => (
                  <div key={item.name} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <span className="text-white font-medium">{item.name}</span>
                    <span className="font-mono text-red-400">{item.cost}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-text-secondary font-medium">Total Monthly</span>
                <span className="font-mono text-2xl text-red-400 font-bold">$125+/mo</span>
              </div>
            </div>

            {/* fans.audio */}
            <div className="glass-card p-6 sm:p-8 border-electric-cyan/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-electric-cyan/5 rounded-full blur-[60px]" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-6">
                  <CheckCircle className="w-5 h-5 text-electric-cyan" />
                  <h3 className="font-mono text-sm text-electric-cyan uppercase tracking-wider">fans.audio Way (Sovereign OS)</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { name: "Transmissions", replaces: "Email Marketing" },
                    { name: "Intelligence", replaces: "Data Analytics" },
                    { name: "Growth Engine", replaces: "Link Generators" },
                    { name: "The Vault", replaces: "File Hosting" },
                  ].map((item) => (
                    <div key={item.name} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-electric-cyan" />
                        <span className="text-white font-medium">{item.name}</span>
                      </div>
                      <span className="font-mono text-electric-cyan text-sm">Included</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-text-secondary font-medium">Total Monthly</span>
                  <span className="font-mono text-2xl text-electric-cyan font-bold">$0/mo</span>
                </div>
                <p className="text-xs text-text-muted mt-3">No subscriber tax. No export fees. You own everything.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function DigitalPressingSection() {
  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 dithered-gradient opacity-35" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Make a record again.</h2>
            <p className="text-text-secondary text-lg leading-relaxed mb-4">
              Music used to be something you could hold, explore and keep. The artwork, the release and the memories attached to it. Streaming made music accessible everywhere, but collecting made it personal.
            </p>
            <p className="text-text-secondary text-lg leading-relaxed mb-4">Pressings bring that idea into the digital world.</p>
            <p className="text-text-secondary text-lg leading-relaxed">
              Create editions of your music that fans can discover and add to their collections. Give your releases something more than a moment in a playlist.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.article variants={fadeInUp} className="glass-card-hover p-7">
              <p className="terminal-label mb-2">FOR THE ARTIST</p>
              <h3 className="text-xl font-bold mb-3">More than another upload.</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Create a release with its own identity, share it through Captures and give fans something meaningful to collect.
              </p>
            </motion.article>

            <motion.article variants={fadeInUp} className="glass-card-hover p-7">
              <p className="terminal-label mb-2">FOR THE FAN</p>
              <h3 className="text-xl font-bold mb-3">More than another stream.</h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                Build a personal collection of releases from the artists you love. A record of the music you have discovered and chosen to keep close.
              </p>
            </motion.article>
          </motion.div>

          <motion.p variants={fadeInUp} className="text-center text-white font-semibold mt-10 text-lg">
            Your music creates the connection. Pressings give it somewhere to live.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

const intelDatabaseEntries = [
  {
    q: 'IS_THIS_JUST_ANOTHER_PATREON_CLONE?',
    a: 'No. Platforms like Patreon and Bandcamp are Destinations—you send your fans to their domain, where they own the data, the customer relationship, and the "Buy" button. fans.audio is a Protocol. When you use our OS, you aren\'t sending fans to a shared marketplace; you are inviting them into your own Sovereign Node. We provide the "Invisible Infrastructure" (the Vaults, the distribution, the payments), but the relationship is direct. If we disappeared tomorrow, your fan data and your "Pressings" would remain yours. We don\'t want to be the middleman; we want to be the engine.',
  },
  {
    q: 'HOW_DO_PEOPLE_JOIN_MY_FAN_CIRCLE?',
    a: 'We provide you with a powerful set of tools called "Captures." These are custom-branded landing pages and forms designed to convert listeners into followers. You use Captures in your advertising, on your flyers (via QR codes), and across social media to promote your tracks. To "unlock" a stream or a free pressing, the fan registers their email address with fans.audio. This instantly adds them to your Fan Circle, giving you a direct, permanent line of communication to your most engaged supporters—no algorithms required.',
  },
  {
    q: 'DO_YOU_DO_PRE-SAVE_CAMPAIGNS?',
    a: 'Simple answer. No. The gurus and platforms are selling you musical snake oil. Pre-saves are ineffective, expensive to drive users to, and a by-product of the gatekeeper system we are breaking away from. Pre-saves are not active engagement—they are batch processes, let\'s call it what it really is, saving to user accounts from a single IP address in seconds, a bot. We believe in Active Transmission. Instead of a vanity "Save," we prioritize getting the fan\'s email address through a Capture. On release day, you send a direct transmission with the streaming link. This ensures a real human clicks a real link, triggering the high-intent signals the algorithms actually value. We make that possible, and we make it easy.',
  },
  {
    q: 'CAN_I_UPLOAD_FOR_FANS_TO_ACCESS_MY_TRACKS_FOR_FREE?',
    a: 'Absolutely. In fact, we recommend it. Any fan who joins your Fan Circle gains immediate access to your Free Digital Pressings. We actively encourage you to use free assets as an incentive to bring fans into your private ecosystem. Once they are inside your Circle, they become part of your direct communications—allowing you to nurture the relationship and eventually upsell premium Vaults, exclusive Stems, or limited-run Pressings in exchange for Fan Credits, which we then convert into hard cash for you.',
  },
  {
    q: 'DO_FANS_NEED_TO_DOWNLOAD_AN_APP?',
    a: 'No. fans.audio is built on Frictionless Web-Standard architecture. Your fans access your music and your Vaults through any mobile or desktop browser via open web APIs. There are no app store middle-men, no 30% "Apple Tax" on your credits, and no friction between your social media link and the music. It is a seamless, white-label Progressive Web Experience.',
  },
  {
    q: 'DO_YOU_DISTRIBUTE_TO_STREAMING_SERVICES?',
    a: 'Yes. Through the BMIC Infrastructure, we distribute to 150+ Digital Service Providers (DSPs), including Spotify, Apple Music, and Tidal. However, we view streaming as a discovery tool, not a retirement plan. The real economy happens inside your Fan Circle.',
  },
  {
    q: 'WHAT_IS_A_PRESSING?',
    a: 'A Pressing is the act of "minting" your digital assets into a Vault. Unlike a simple upload, a Pressing creates a unique, high-fidelity instance of your work (WAVs, Stems, Visuals) that fans can unlock using Credits. It is the digital equivalent of a vinyl run, but with infinite utility.',
  },
  {
    q: 'CAN_I_MIGRATE_MY_OLD_MUSIC_HERE?',
    a: 'Yes. We prioritize artists with deep back-catalogs. Our ISRC Porting Protocol allows you to port your legacy ISRCs, metadata, and master assets from existing distribution providers into the Sovereign OS—preserving your streaming history and play counts. We help you turn your "streaming history" into a "Sovereign Catalog" without losing a single data point.',
  },
  {
    q: 'IS_IT_REALLY_FREE_TO_USE?',
    a: 'During the Stealth Pilot, there are no upfront costs or subscription fees for selected artists. We operate on a transparent 85/15 split on fan-direct revenue. We only make money when you are actually generating value from your community.',
  },
  {
    q: 'WHO_OWNS_MY_MASTERS?',
    a: 'You do. 100%. Always. We are a B2B Infrastructure provider, not a record label. We have zero claim to your Intellectual Property. Our job is to move your music, not own it.',
  },
  {
    q: 'WHAT_IS_THE_TOOL_TAX?',
    a: 'It\'s the hidden cost of the old music industry. Most artists pay a "Maintenance Tax" to keep music on streaming sites and a "Discovery Tax" to social media companies just to show their work to their own fans. When you calculate your annual fees and ad spend against your streaming royalties, most artists are operating at a net loss. The Sovereign OS eliminates the Tool Tax by replacing rented reach with owned Fan Circles and replacing flat fees with a performance-based infrastructure model.',
  },
  {
    q: 'SO_I_CAN_REALLY_CANCEL_MY_OTHER_SUBSCRIPTIONS?',
    a: null as unknown as string,
    richContent: (
      <>
        <p className="mb-3">Simple answer. Yes. During the Stealth Pilot, we provide the integrated infrastructure that replaces the fragmented &quot;Tool Tax&quot; you pay every month. You can effectively terminate your subscriptions for:</p>
        <ul className="space-y-2 mb-4 ml-1">
          <li className="flex items-start gap-2">
            <span className="text-electric-cyan/60 mt-0.5">▸</span>
            <span><span className="text-white/80 font-medium">Communication:</span> <s className="text-text-muted">MailChimp / ConvertKit</s> <span className="text-electric-cyan/80 text-xs font-mono ml-1">(Replaced by Fan Circles)</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-electric-cyan/60 mt-0.5">▸</span>
            <span><span className="text-white/80 font-medium">Marketing:</span> <s className="text-text-muted">Feature.fm / Linktree</s> <span className="text-electric-cyan/80 text-xs font-mono ml-1">(Replaced by Captures &amp; Nodes)</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-electric-cyan/60 mt-0.5">▸</span>
            <span><span className="text-white/80 font-medium">Distribution:</span> <s className="text-text-muted">LANDR / DistroKid</s> <span className="text-electric-cyan/80 text-xs font-mono ml-1">(Replaced by BMIC Infrastructure)</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-electric-cyan/60 mt-0.5">▸</span>
            <span><span className="text-white/80 font-medium">Storage/Delivery:</span> <s className="text-text-muted">Dropbox / WeTransfer Pro</s> <span className="text-electric-cyan/80 text-xs font-mono ml-1">(Replaced by Sovereign Vaults)</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-electric-cyan/60 mt-0.5">▸</span>
            <span><span className="text-white/80 font-medium">Storefronts:</span> <s className="text-text-muted">Shopify / Patreon</s> <span className="text-electric-cyan/80 text-xs font-mono ml-1">(Replaced by Direct Credits)</span></span>
          </li>
        </ul>
        <p>Our goal is to consolidate your overhead into one unified Operating System. You stop paying for 10 different logins; you start using one protocol for free.</p>
      </>
    ),
  },
];

function IntelDatabaseSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="intel" className="section-padding relative">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="terminal-label mb-4 block">architecture::sovereign</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Your Sovereign Artist OS.
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto leading-relaxed">
              fans.audio is not just another account on a shared platform.
              <br className="hidden sm:block" />
              Your website, catalogue, fan relationships and release infrastructure are coordinated as one operating system.
            </p>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto mt-4 leading-relaxed">
              The intel below covers practical questions from artists evaluating whether this model fits how they want to build.
            </p>
          </motion.div>

          {/* Accordion */}
          <motion.div variants={fadeInUp} className="space-y-2">
            {intelDatabaseEntries.map((entry, idx) => {
              const isOpen = openIndex === idx;
              const isLast = idx === intelDatabaseEntries.length - 1;
              return (
                <div
                  key={idx}
                  className={`rounded-xl border transition-all duration-300 ${
                    isOpen
                      ? 'border-electric-cyan/30 bg-electric-cyan/[0.03]'
                      : 'border-white/5 bg-surface/20 hover:border-white/10'
                  } ${isLast && isOpen ? 'ring-1 ring-electric-cyan/20' : ''}`}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-electric-cyan/40 text-xs font-mono shrink-0">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className={`font-mono text-sm sm:text-base tracking-wide truncate ${
                        isOpen ? 'text-electric-cyan' : 'text-white'
                      }`}>
                        {entry.q}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 transition-transform duration-300 ${
                        isOpen ? 'text-electric-cyan rotate-180' : 'text-text-muted'
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      className="px-5 pb-5"
                    >
                      <div className={`border-t pt-4 ${isLast ? 'border-electric-cyan/20' : 'border-white/5'}`}>
                        {'richContent' in entry && entry.richContent ? (
                          <div className={`text-sm leading-relaxed ${isLast ? 'text-white font-medium' : 'text-text-secondary'}`}>
                            {entry.richContent}
                          </div>
                        ) : (
                          <p className={`text-sm leading-relaxed ${isLast ? 'text-white font-medium' : 'text-text-secondary'}`}>
                            {entry.a}
                          </p>
                        )}
                        {isLast && (
                          <div className="mt-4 flex items-center gap-2">
                            <div className="h-px flex-1 bg-electric-cyan/20" />
                            <span className="text-[10px] font-mono text-electric-cyan/60 uppercase tracking-widest">full_stop</span>
                            <div className="h-px flex-1 bg-electric-cyan/20" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function OnboardingSection({ onLaunch }: { onLaunch?: () => void }) {
  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 waveform-bg opacity-45" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-electric-cyan/5 rounded-full blur-[140px]" />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.div variants={fadeInUp} className="mb-4">
            <span className="terminal-label">founding::artists</span>
          </motion.div>

          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Help shape what comes next.
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-lg text-text-secondary mb-4 max-w-2xl mx-auto">
            fans.audio is growing with a small group of independent artists who want to build something lasting around their music.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-lg text-text-secondary mb-10 max-w-2xl mx-auto">
            We are inviting artists to establish their home, bring their audience and help shape the platform as it develops.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex justify-center">
            <button onClick={onLaunch} className="btn-primary text-base inline-flex items-center">
              <Terminal className="w-5 h-5 mr-2" />
              Apply to Join
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-6 h-6 object-contain" />
            <span className="font-mono font-bold text-sm">fans<span className="text-electric-cyan">.audio</span></span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/for-fans" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">For Fans</a>
            <a href="/for-artists" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">For Artists</a>
            <a href="/manifesto" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Manifesto</a>
            <a href="/terms-of-service" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Terms of Service</a>
            <a href="/privacy-policy" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Privacy Policy</a>
          </div>
        </div>
        <div className="mt-8 text-center space-y-2">
          <p className="text-xs text-text-muted">
            fans.audio is a trading style of Boogie Microphone Limited.
          </p>
          <p className="font-mono text-xs text-text-muted">
            Contact: admin@bmic.music
          </p>
        </div>
      </div>
    </footer>
  );
}

// --- LANDING PAGE ---

function ForArtistsPage() {
  const [showRequestAccess, setShowRequestAccess] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? null;

    document.title = 'For Artists | fans.audio';
    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Create a home for your music, grow direct fan relationships, and share collectible digital releases with fans.audio.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== null) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  if (showRequestAccess) {
    return <RequestAccessPage />;
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <Navbar ctaLabel="Artist Login" ctaHref="/login" activeNav="for-artists" />
      <HeroSection onLaunch={() => setShowRequestAccess(true)} />
      <ArtistBridgeSection />
      <BuildSomethingYouOwnSection />
      <AudienceRelationshipSection />
      <DigitalPressingSection />
      <OnboardingSection onLaunch={() => setShowRequestAccess(true)} />
      <Footer />
    </div>
  );
}

// --- MAIN APP (Multi-Tenant Router) ---

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-electric-cyan mb-4">404</h1>
        <p className="text-text-secondary text-lg mb-6">Page not found</p>
        <a href="/" className="btn-primary inline-flex items-center">
          Go Home
        </a>
      </div>
    </div>
  );
}

function ArtistPageWithAuth() {
  const { user, token, isChecking, setChecking } = useAuthStore();
  const tenant = useTenant();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const sessionCheckFired = useRef(false);

  // Safety timeout — never hang on loading state indefinitely
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!accessChecked) {
        console.warn('[ArtistPageWithAuth] Safety timeout reached');
        useAuthStore.getState().setChecking(false);
        setAccessChecked(true);
      }
    }, 6000);
    return () => clearTimeout(timeout);
  }, []);

  // Session restoration: wait for hydration, then validate stored token
  useEffect(() => {
    if (!isChecking) return;
    if (sessionCheckFired.current) return;

    const runCheck = () => {
      if (sessionCheckFired.current) return;
      sessionCheckFired.current = true;

      const { token: currentToken } = useAuthStore.getState();
      if (!currentToken) {
        useAuthStore.getState().setChecking(false);
        return;
      }

      getMe(currentToken).then(res => {
        if (res.ok) return res.json();
        throw new Error('Session expired');
      }).then((data: any) => {
        if (data.user) {
          useAuthStore.getState().login(data.user, currentToken);
        } else {
          useAuthStore.getState().logout();
        }
      }).catch(() => {
        useAuthStore.getState().logout();
      });
    };

    if (useAuthStore.persist?.hasHydrated?.()) {
      runCheck();
    } else if (useAuthStore.persist?.onFinishHydration) {
      const unsub = useAuthStore.persist.onFinishHydration(() => {
        runCheck();
        unsub();
      });
      return () => { unsub(); };
    } else {
      runCheck();
    }
  }, [isChecking]);

  useEffect(() => {
    if (isChecking) return;
    
    // Require authentication for tenant pages
    if (!token || !user) {
      setHasAccess(false);
      setAccessChecked(true);
      return;
    }

    // Check access if user is logged in
    if (tenant.isTenant) {
      checkAccess(tenant.slug, token!).then(res => res.json()).then((data: any) => {
        setHasAccess(data.hasAccess);
        setAccessDenied(!data.hasAccess);
        setAccessChecked(true);
      }).catch(() => {
        setHasAccess(false);
        setAccessChecked(true);
      });
    } else {
      setHasAccess(false);
      setAccessChecked(true);
    }
  }, [tenant.isTenant, tenant.slug, token, user, isChecking]);

  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="text-text-muted">Loading...</div>
      </div>
    );
  }

  if (accessDenied) {
    return <NotFoundPage />;
  }

  // Not authenticated — show login page
  if (!hasAccess && !accessDenied) {
    return <TenantLoginPage slug={tenant.slug || ''} />;
  }

  return <ArtistPage />;
}

function AdminRoute() {
  const { user, token, isChecking, setChecking, login } = useAuthStore();
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    // Try to restore session from stored token
    const storedToken = token;
    if (!storedToken) {
      setRestoring(false);
      setChecking(false);
      return;
    }

    getMe(storedToken).then(res => {
      if (res.ok) return res.json();
      throw new Error('Session expired');
    }).then((data: any) => {
      if (data.user && data.user.role === 'admin') {
        login(data.user, storedToken);
      } else {
        useAuthStore.getState().logout();
      }
    }).catch(() => {
      useAuthStore.getState().logout();
    }).finally(() => {
      setRestoring(false);
      setChecking(false);
    });
  }, []);

  if (restoring || isChecking) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="text-text-muted">Loading...</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <AdminLoginPage />;
  }

  // Sub-route: /admin/settings - removed (domain now comes from BASE_DOMAIN secret)
  const adminPath = window.location.pathname;
  if (adminPath === '/admin/templates') {
    return <AdminTemplatesPage />;
  }
  if (adminPath === '/admin/messages') {
    return <AdminMessagesPage />;
  }
  if (adminPath === '/admin/mint-queue') {
    return <AdminMintQueuePage />;
  }
  if (adminPath === '/admin/compliance') {
    return <AdminCompliancePage />;
  }
  if (adminPath === '/admin/compliance-audit') {
    return <AdminComplianceAuditPage />;
  }

  return <AdminDashboard />;
}

function TenantAuthGuard({ children }: { children: (slug: string) => React.ReactNode }) {
  const { user, token, isChecking } = useAuthStore();
  const tenant = useTenant();
  const [state, setState] = useState<'loading' | 'authenticated' | 'login'>('loading');
  const [resolvedSlug, setResolvedSlug] = useState('');
  const authFlowFired = useRef(false);

  // Safety timeout — never hang on loading state indefinitely
  useEffect(() => {
    const timeout = setTimeout(() => {
      setState((prev) => {
        if (prev === 'loading') {
          console.warn('[TenantAuthGuard] Safety timeout reached');
          useAuthStore.getState().setChecking(false);
          return 'login';
        }
        return prev;
      });
    }, 8000);
    return () => clearTimeout(timeout);
  }, []);

  // Unified auth flow: restore session → resolve slug → check access
  useEffect(() => {
    if (authFlowFired.current) return;

    const runAuthFlow = async (currentToken: string) => {
      if (authFlowFired.current) return;
      authFlowFired.current = true;

      try {
        // Step 1: Validate session and get fresh user data from backend
        const meRes = await getMe(currentToken);
        if (!meRes.ok) {
          useAuthStore.getState().logout();
          setState('login');
          return;
        }
        const meData = await meRes.json() as any;
        if (!meData.user) {
          useAuthStore.getState().logout();
          setState('login');
          return;
        }

        // Update store with fresh user data
        useAuthStore.getState().login(meData.user, currentToken);
        const freshUser = meData.user;

        // Step 2: Resolve slug
        let slug = tenant.isTenant ? tenant.slug : (freshUser.tenant?.slug || freshUser.tenantSlug || '');

        // Admin impersonation fallback
        if (!slug && freshUser.role === 'admin') {
          const params = new URLSearchParams(window.location.search);
          const asSlug = params.get('as');
          if (asSlug) {
            slug = asSlug;
            setAdminAsSlug(asSlug);
          } else {
            slug = getAdminAsSlug() || '';
          }
        }

        if (!slug) {
          setState('login');
          return;
        }

        // Step 3: Check access
        const accessRes = await checkAccess(slug, currentToken);
        const accessData = await accessRes.json() as any;

        if (accessData.hasAccess) {
          setResolvedSlug(slug);
          setState('authenticated');
        } else {
          useAuthStore.getState().logout();
          setState('login');
        }
      } catch {
        useAuthStore.getState().logout();
        setState('login');
      }
    };

    const attemptAuth = () => {
      const { token: storedToken } = useAuthStore.getState();
      if (!storedToken) {
        useAuthStore.getState().setChecking(false);
        setState('login');
        return;
      }
      runAuthFlow(storedToken);
    };

    // Wait for zustand hydration before reading token
    if (useAuthStore.persist?.hasHydrated?.()) {
      attemptAuth();
    } else if (useAuthStore.persist?.onFinishHydration) {
      const unsub = useAuthStore.persist.onFinishHydration(() => {
        attemptAuth();
        unsub();
      });
      return () => { unsub(); };
    } else {
      attemptAuth();
    }
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-electric-cyan/30 border-t-electric-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (state === 'login') {
    return <TenantLoginPage slug="" />;
  }

  return <>{children(resolvedSlug)}</>;
}

function App() {
  const tenant = useTenant();
  const path = window.location.pathname;

  // Route: /manifesto
  if (path === '/manifesto') {
    return <ManifestoPage />;
  }

  // Route: /for-fans
  if (path === '/for-fans') {
    return <ForFansPage />;
  }

  // Route: /for-artists or /artists
  if (path === '/for-artists' || path === '/artists') {
    return <ForArtistsPage />;
  }

  // Route: /request-access
  if (path === '/request-access') {
    return <RequestAccessPage />;
  }

  // Route: /privacy-policy
  if (path === '/privacy-policy') {
    return <PrivacyPolicyPage />;
  }

  // Route: /login — artist login page (resolves tenant from backend after OTP)
  if (path === '/login') {
    return <TenantLoginPage slug="" />;
  }

  // Route: /terms-of-service
  if (path === '/terms-of-service') {
    return <TermsOfServicePage />;
  }

  // Route: /artist-enrolment/:artistReference
  const enrolmentMatch = path.match(/^\/artist-enrolment\/([a-z0-9]+)$/);
  if (enrolmentMatch) {
    return <ArtistEnrolmentPage artistReference={enrolmentMatch[1]} />;
  }

  // Route: /admin or /admin/*
  if (path === '/admin' || path.startsWith('/admin/')) {
    // Clear admin impersonation context when returning to admin panel
    clearAdminAsSlug();
    return <AdminRoute />;
  }

  // Route: /dashboard (protected - artist dashboard)
  if (path === '/dashboard') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => <ArtistPage slug={slug} />}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /track-editor/:trackRef (protected + onboarding guard)
  const editorMatch = path.match(/^\/track-editor\/([^/]+)$/);
  if (editorMatch) {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasProfileImage">
              <TrackEditorPage slug={slug} trackRef={editorMatch[1]} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /catalog (protected + onboarding guard)
  if (path === '/catalog') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasProfileImage">
              <CatalogPage slug={slug} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /growth (protected + onboarding guard)
  if (path === '/growth') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasPublishedVault">
              <GrowthPage slug={slug} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /fans (protected + onboarding guard)
  if (path === '/fans') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasPublishedVault">
              <FansPage slug={slug} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /transmissions/:id (editor - must be before list route)
  const transmissionEditorMatch = path.match(/^\/transmissions\/([^/]+)$/);
  if (transmissionEditorMatch) {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasAtLeastOneFan">
              <TransmissionEditorPage slug={slug} transmissionId={transmissionEditorMatch[1]} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /transmissions (protected + onboarding guard)
  if (path === '/transmissions') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasAtLeastOneFan">
              <TransmissionsPage slug={slug} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /pressings/:id (vault editor - must be before list route)
  const vaultEditorMatch = path.match(/^\/pressings\/([^/]+)$/);
  if (vaultEditorMatch) {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasVerifiedTrack">
              <VaultEditorPage slug={slug} vaultId={vaultEditorMatch[1]} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /pressings (protected + onboarding guard)
  if (path === '/pressings') {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => (
            <OnboardingGuard slug={slug} requiredKey="hasVerifiedTrack">
              <PressingsPage slug={slug} />
            </OnboardingGuard>
          )}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /player/:trackRef (protected)
  const playerMatch = path.match(/^\/player\/([^/]+)$/);
  if (playerMatch) {
    return (
      <TenantProvider>
        <TenantAuthGuard>
          {(slug) => <PlayerPage slug={slug} trackRef={playerMatch[1]} />}
        </TenantAuthGuard>
      </TenantProvider>
    );
  }

  // Route: /pressing/:id (View Pressing page)
  const pressingViewMatch = path.match(/^\/pressing\/([^/]+)$/);
  if (pressingViewMatch) {
    return (
      <TenantProvider>
        <ViewVaultPage vaultId={pressingViewMatch[1]} />
      </TenantProvider>
    );
  }

  // Legacy alias: /vault/:id -> View Pressing page
  const vaultViewMatch = path.match(/^\/vault\/([^/]+)$/);
  if (vaultViewMatch) {
    return (
      <TenantProvider>
        <ViewVaultPage vaultId={vaultViewMatch[1]} />
      </TenantProvider>
    );
  }

  // Route: /artist/:slug (Artist Public Profile page)
  const artistProfileMatch = path.match(/^\/artist\/([^/]+)$/);
  if (artistProfileMatch) {
    return (
      <TenantProvider>
        <ArtistProfilePage artistSlug={artistProfileMatch[1]} />
      </TenantProvider>
    );
  }

  // Route: /fan-login (Fan Login page with redirect support)
  if (path === '/fan-login') {
    return (
      <TenantProvider>
        <FanLoginPage />
      </TenantProvider>
    );
  }

  // Route: /fanzone/:userReference
  const fanZoneMatch = path.match(/^\/fanzone\/([a-z0-9]+)$/);
  if (fanZoneMatch) {
    return (
      <TenantProvider>
        <FanZonePage userReference={fanZoneMatch[1]} />
      </TenantProvider>
    );
  }

  // Route: /my-collection/:userReference (fan email links)
  const myCollectionMatch = path.match(/^\/my-collection\/([a-z0-9]+)$/);
  if (myCollectionMatch) {
    return (
      <TenantProvider>
        <FanZonePage userReference={myCollectionMatch[1]} />
      </TenantProvider>
    );
  }

  // Route: /:tenantSlug/:captureSlug (public capture page with explicit tenant in path)
  const captureWithTenantMatch = path.match(/^\/([a-z0-9]([a-z0-9-]*[a-z0-9])?)\/([a-z0-9]([a-z0-9-]*[a-z0-9])?)$/);
  if (captureWithTenantMatch) {
    const pathTenantSlug = captureWithTenantMatch[1];
    const pathCaptureSlug = captureWithTenantMatch[3];
    // Skip if first segment is a known route prefix
    const routePrefixes = ['admin', 'player', 'track-editor', 'fanzone', 'vault', 'pressing', 'artist',
      'transmissions', 'pressings', 'for-fans', 'for-artists', 'artists', 'my-collection'];
    if (!routePrefixes.includes(pathTenantSlug)) {
      return (
        <TenantProvider>
          <CapturePage tenantSlug={pathTenantSlug} captureSlug={pathCaptureSlug} />
        </TenantProvider>
      );
    }
  }

  // Route: /:captureSlug (public capture page - single segment, not reserved)
  const captureMatch = path.match(/^\/([a-z0-9]([a-z0-9-]*[a-z0-9])?)$/);
  if (captureMatch) {
    const potentialCapture = captureMatch[1];
    // Skip reserved paths
    const reserved = ['admin', 'manifesto', 'signup', 'request-access', 'artist-enrolment', 
      'privacy-policy', 'terms-of-service', 'login', 'catalog', 'growth', 'fans', 
      'transmissions', 'pressings', 'player', 'track-editor', 'fanzone', 'dashboard',
      'vault', 'pressing', 'artist', 'fan-login', 'for-fans', 'for-artists', 'artists', 'my-collection'];
    if (!reserved.includes(potentialCapture)) {
      // Resolve tenant from domain if available, otherwise pass null (backend resolves by capture slug alone)
      const tenantSlug = tenant.isTenant ? tenant.slug : null;
      return (
        <TenantProvider>
          <CapturePage tenantSlug={tenantSlug} captureSlug={potentialCapture} />
        </TenantProvider>
      );
    }
  }

  // Root route: neutral homepage (tenant domains still go to tenant-specific flow)
  return (
    <TenantProvider>
      {tenant.isTenant ? <ArtistPageWithAuth /> : <HomePage />}
    </TenantProvider>
  );
}

/**
 * Root route handler (legacy): redirects authenticated artists to /dashboard.
 * Kept for backward compatibility if someone bookmarked / while logged in.
 */
function RootRoute() {
  const { user, token, isChecking } = useAuthStore();

  useEffect(() => {
    if (isChecking) return;
    if (token && user) {
      const slug = user.tenant?.slug || (user as any).tenantSlug;
      if (user.role === 'artist' && slug) {
        window.location.href = '/dashboard';
      }
    }
  }, [token, user, isChecking]);

  return <HomePage />;
}

export default App;
